import { Component, inject, OnDestroy } from '@angular/core';
import { combineLatest, distinctUntilChanged, merge, Observable, ReplaySubject, Subject } from 'rxjs';
import { Pageable } from '../../common/paging/pageable';
import { Page } from '../../domain/utils/page';
import { PlanwertFilter } from '../../service/planwert/planwert-filter';
import { TnbUser } from '../../service/auth/tnb-user';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { TeilgebietSelectionService } from '../../service/common/teilgebiet/teilgebiet-selection.service';
import { PlanwertService } from '../../service/planwert/planwert.service';
import {
    debounceTime,
    filter,
    map,
    shareReplay,
    skip,
    startWith,
    switchMap,
    takeWhile,
} from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../service/common/routing/routing.service';
import moment from 'moment';
import { Moment } from 'moment/moment';
import { PlanwertListEntry } from '../../domain/model/planwert-list-entry.model';
import { distinctUntilFilterChanged } from '../../common/util/helper';

@Component({
    selector: 'redispatch-planwert-list',
    templateUrl: './planwert-list.component.html',
    styleUrls: [
        './planwert-list.component.scss',
    ],
    standalone: false,
})
export class PlanwertListComponent implements OnDestroy {

    public readonly PAGE_SIZE = 50;
    public readonly STATUS_SORT_OVERWRITE = 'status,dataTransferDate';

    public page$: Observable<Pageable>;
    public filter$: Observable<PlanwertFilter>;
    public planwertPage$: Observable<Page<PlanwertListEntry>>;

    public readonly profile: TnbUser;

    private componentDestroyed = false;

    private pageFromUserInteraction$: Observable<Pageable>;
    private pageEventFromUserInteraction$ = new ReplaySubject<PageEvent>(1);
    private sortFromUserInteraction$ = new ReplaySubject<Sort>(1);
    private filterFromUserInteraction$ = new Subject<PlanwertFilter>();

    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private routeService = inject(RoutingService);
    private planwertService = inject(PlanwertService);
    private teilgebietSelectionService = inject(TeilgebietSelectionService);

    constructor() {
        this.profile = this.route.snapshot.data.profile;

        this.setupFilterObservable();
        this.setupPageObservable();
        this.setupPlanwertObservable();
    }

    ngOnDestroy(): void {
        this.componentDestroyed = true;
    }

    private setupPageObservable() {
        this.pageFromUserInteraction$ = combineLatest([
            this.sortFromUserInteraction$,
            this.pageEventFromUserInteraction$,
        ])
            .pipe(
                map(params => {
                    const pageable = new Pageable();

                    const sort = params[0];
                    const page = params[1];

                    pageable.sort = sort?.active || '';
                    pageable.direction = sort?.direction || '';
                    pageable.page = page?.pageIndex || 0;

                    return pageable;
                }),
                shareReplay(1),
            );

        this.route.queryParams
            .pipe(
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(params => {
                this.sortFromUserInteraction$.next({
                    active: params['sort'],
                    direction: params['direction'],
                });
                this.pageEventFromUserInteraction$.next({
                    pageIndex: +params['page'],
                } as PageEvent);
            });

        this.page$ = this.pageFromUserInteraction$
            .pipe(
                distinctUntilFilterChanged(),
                shareReplay(1),
            );
    }

    private setupFilterObservable() {
        const filterFromQueryParam$ = this.route.queryParams.pipe(
            map((params: any) => {
                const pwFilter = new PlanwertFilter();
                if (params) {
                    pwFilter.netzbetreiberName = params['netzbetreiberName'];
                    pwFilter.stadtwerkeId = params['stadtwerkeId'];
                    pwFilter.erzeugungsanlageDokumentenId = params['erzeugungsanlageDokumentenId'];
                    pwFilter.erzeugungsanlageTechnischeResourceId = params['erzeugungsanlageTechnischeResourceId'];
                    pwFilter.erzeugungsanlageBezVnbIntern = params['erzeugungsanlageBezVnbIntern'];
                    pwFilter.netzzuordnungNetzverknuepfungspunkte = params['netzzuordnungNetzverknuepfungspunkte'];
                    pwFilter.dataTransferDate = this.parseToDataTransferDate(params['dataTransferDateStart'], params['dataTransferDateEnd']);
                    pwFilter.type = params['type'] ? JSON.parse(params['type']) : undefined;
                    pwFilter.status = params['status'] ? JSON.parse(params['status']) : undefined;
                }

                if (this.profile.netzbetreiberType !== 'WESTNETZ') {
                    pwFilter.stadtwerkeId = this.teilgebietSelectionService.getSelectedStadtwerkeId();
                }
                return pwFilter;
            }),
        );

        this.filter$ = merge(
            this.filterFromUserInteraction$,
            filterFromQueryParam$,
        ).pipe(
            distinctUntilFilterChanged(),
            shareReplay(1),
        );
    }

    private parseToDataTransferDate(possibleDateStart: string | undefined, possibleDateEnd: string | undefined): {
        start?: Moment,
        end?: Moment,
    } {
        const start = this.parseToDate(possibleDateStart);
        const end = this.parseToDate(possibleDateEnd);

        return {
            start,
            end,
        };
    }

    private parseToDate(possibleDate: string | undefined): Moment | undefined {
        const date = moment(possibleDate || null); // undefined means now
        return date.isValid() ? date : undefined;
    }

    private setupPlanwertObservable() {
        // Goto first page, when filter changes
        this.filterFromUserInteraction$
            .pipe(
                skip(1),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(_filter => {
                const page = new PageEvent();
                page.pageIndex = 0;
                this.pageEventFromUserInteraction$.next(page);
            });

        combineLatest([
            this.pageFromUserInteraction$,
            this.filterFromUserInteraction$,
        ])
            .pipe(
                filter(() => {
                    const currentParams = this.route.snapshot.queryParams;
                    return !currentParams['erzeugungsanlage'];
                }),
                map(param => {
                    const pageable = param[0];
                    const pwFilter = param[1];
                    return this.routeService.planwerteTable(pageable, pwFilter);
                }),
                distinctUntilChanged(),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(url => {
                this.router.navigateByUrl(url);
            });

        this.planwertPage$ = combineLatest([
            this.page$,
            this.filter$,
            this.planwertService.refreshRequested$.pipe(startWith(null)),
        ]).pipe(
            debounceTime(0),
            takeWhile(() => !this.componentDestroyed),
            switchMap(([
                page,
                pwFilter,
            ]) => {
                if (page.sort === 'status') {
                    page = {
                        ...page,
                        sort: this.STATUS_SORT_OVERWRITE,
                    };
                }
                return this.planwertService.listPlanwerte(page, pwFilter);
            }),
        );
    }

    filterChanged(pwFilter: PlanwertFilter) {
        this.filterFromUserInteraction$.next(pwFilter);
    }

    pageChange($event: PageEvent) {
        this.pageEventFromUserInteraction$.next($event);
    }

    sortChange($event: Sort) {
        this.sortFromUserInteraction$.next($event);
    }
}
